// frontend/shared-ui/src/components/Dropdown.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const DropdownContext = createContext(null);

export function Dropdown({
  trigger,
  children,
  align = "right",
  direction = "down",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const menuClasses = [
    "fh-dropdown__menu",
    align === "left" ? "fh-dropdown__menu--left" : "",
    direction === "up" ? "fh-dropdown__menu--up" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <DropdownContext.Provider value={{ close: () => setOpen(false) }}>
      <div className={`fh-dropdown ${className}`} ref={ref}>
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
        >
          {trigger}
        </div>
        {open && <div className={menuClasses}>{children}</div>}
      </div>
    </DropdownContext.Provider>
  );
}

export function DropdownHeader({ name, email, avatar }) {
  return (
    <div className="fh-dropdown__header">
      <div className="d-flex align-items-center gap-2">
        {avatar}
        <div style={{ minWidth: 0 }}>
          <div className="fh-dropdown__name">{name}</div>
          {email && <div className="fh-dropdown__email">{email}</div>}
        </div>
      </div>
    </div>
  );
}

export function DropdownItem({
  icon,
  children,
  onClick,
  variant = "default",
  as = "button",
  href,
  ...rest
}) {
  const { close } = useContext(DropdownContext) || {};

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) onClick(e);
    close?.();
  };

  const classes = [
    "fh-dropdown__item",
    variant === "danger" ? "fh-dropdown__item--danger" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (as === "a" || href) {
    return (
      <a href={href} className={classes} onClick={handleClick} {...rest}>
        {icon && <span className="fh-dropdown__item-icon">{icon}</span>}
        <span>{children}</span>
      </a>
    );
  }

  return (
    <button type="button" className={classes} onClick={handleClick} {...rest}>
      {icon && <span className="fh-dropdown__item-icon">{icon}</span>}
      <span>{children}</span>
    </button>
  );
}

export function DropdownDivider() {
  return <hr className="fh-dropdown__divider" />;
}
